import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon as XIcon, ArrowDownTrayIcon as DownloadIcon, EyeIcon, TrashIcon, UsersIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import firebaseService from '../../services/firebaseService';
import OrdersDashboard from './OrdersDashboard';

const AdminDashboard = React.memo(({ isOpen, onClose }) => {
  const [paidUsers, setPaidUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'orders'

  useEffect(() => {
    if (isOpen) {
      loadPaidUsers();
    }
  }, [isOpen]);

  const loadPaidUsers = async () => {
    setIsLoading(true);
    try {
      const result = await firebaseService.getAllUsers();
      if (result.success) {
        // Filter only paid users and format the data
        const paidUsers = result.data
          .filter(user => user.hasPurchased)
          .map(user => ({
            id: user.uid,
            name: user.name || user.phone || 'Unknown',
            phone: user.phone || 'No Phone',
            email: user.email || 'No Email',
            type: user.accessType === 'web_access' ? 'web' : 'hardcopy',
            payment: {
              amount: user.paymentAmount || 0,
              paidAt: user.accessGrantedAt || user.createdAt
            },
            createdAt: user.createdAt,
            accessType: user.accessType
          }));
        setPaidUsers(paidUsers);
      }
    } catch (error) {
      // Production: Error handled silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await firebaseService.deleteUser(userId);
        loadPaidUsers();
      } catch (error) {
        // Production: Error handled silently
      }
    }
  };

  const exportUserData = async () => {
    try {
      const result = await firebaseService.getAllUsers();
      if (result.success) {
        const data = result.data;
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fijoli-users-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      // Production: Error handled silently
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all w-full max-w-6xl max-h-[90vh]"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Admin Dashboard</h2>
                <p className="text-red-100">Manage Fijoli Online Users & Payments</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={exportUserData}
                  className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg transition-colors"
                >
                  <DownloadIcon className="h-5 w-5" />
                  <span>Export Data</span>
                </button>
                <button
                  onClick={onClose}
                  className="text-white hover:text-red-100 transition-colors"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 mt-4">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'bg-white text-red-600'
                    : 'text-white hover:bg-white hover:text-red-600'
                }`}
              >
                <UsersIcon className="h-5 w-5 inline mr-2" />
                Users
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'orders'
                    ? 'bg-white text-red-600'
                    : 'text-white hover:bg-white hover:text-red-600'
                }`}
              >
                <ShoppingBagIcon className="h-5 w-5 inline mr-2" />
                Orders
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'users' ? (
              <>
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{paidUsers.length}</div>
                    <div className="text-sm text-blue-600">Total Paid Users</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {paidUsers.filter(u => u.type === 'web').length}
                    </div>
                    <div className="text-sm text-green-600">Web Access Users</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {paidUsers.filter(u => u.type === 'hardcopy').length}
                    </div>
                    <div className="text-sm text-purple-600">Hard Copy Orders</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {formatCurrency(paidUsers.reduce((sum, user) => sum + (user.payment?.amount || 0), 0))}
                    </div>
                    <div className="text-sm text-orange-600">Total Revenue</div>
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Paid Users</h3>
              </div>
              
              {isLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading users...</p>
                </div>
              ) : paidUsers.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No paid users found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paidUsers.map((user, index) => (
                        <tr key={user.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.type === 'web' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {user.type === 'web' ? 'Web Access' : 'Hard Copy'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(user.payment?.amount || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.payment?.paidAt || user.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewUser(user)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
              </>
            ) : (
              <OrdersDashboard />
            )}
          </div>
        </motion.div>
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowUserDetails(false)} />
            
            <div className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all w-full max-w-2xl">
              <div className="bg-gray-50 px-6 py-4 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
                  <button
                    onClick={() => setShowUserDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <p className="text-gray-900">{selectedUser.name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <p className="text-gray-900">{selectedUser.phone}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedUser.email || 'No Email'}</p>
                </div>
                
                {selectedUser.alternatePhone && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Alternate Phone</label>
                    <p className="text-gray-900">{selectedUser.alternatePhone}</p>
                  </div>
                )}
                
                {selectedUser.address && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Address</label>
                    <p className="text-gray-900">{selectedUser.address}</p>
                  </div>
                )}
                
                {selectedUser.landmark && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Landmark</label>
                    <p className="text-gray-900">{selectedUser.landmark}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <p className="text-gray-900 capitalize">{selectedUser.type}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Payment Amount</label>
                  <p className="text-gray-900">{formatCurrency(selectedUser.payment?.amount || 0)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Payment Date</label>
                  <p className="text-gray-900">{formatDate(selectedUser.payment?.paidAt || selectedUser.createdAt)}</p>
                </div>
                
                {selectedUser.payment?.razorpayPaymentId && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment ID</label>
                    <p className="text-gray-900 font-mono text-sm">{selectedUser.payment.razorpayPaymentId}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
});

export default AdminDashboard;

